import { useEffect, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { useEntryPointDetection } from './useEntryPointDetection.tsx';
import { useOnboardingRovoConfig, type OnboardingRovoConfig } from './useOnboardingRovoConfig.tsx';

/**
 * Detects the `onboardingEntryPoint` URL param set during the onboarding redirect
 * and fires a `chat-new` publish event to open the Rovo mini-modal once the
 * AISmartButton is ready (`isMiniModalAvailable === true`).
 *
 * This is a one-shot action per page load — `hasFiredRef` prevents re-firing if
 * `isMiniModalAvailable` toggles after the event has been published.
 *
 * Intended for use in Rovo config providers (EditPage, ViewPage, Whiteboard) that
 * need to auto-open the mini-modal chat when a Founding Admin lands after onboarding.
 *
 * Returns the `OnboardingRovoConfig` when an entry point is active, or `null` when
 * no entry point was detected.
 */
export function useOnboardingEntryPointPublish(): OnboardingRovoConfig | null {
	const entryPoint = useEntryPointDetection();
	const publish = usePublish('ai-mate');
	const { isMiniModalAvailable } = useRovoExperienceAPIStore();
	// Tracks whether the publish has already fired for this page load.
	// Using a ref avoids a chained setState inside the effect.
	const hasFiredRef = useRef(false);

	const rovoConfig = useOnboardingRovoConfig({
		skip: entryPoint === null,
		skipAifc: entryPoint === 'cc-onboarding-skip' || entryPoint === 'cc-onboarding-xflow-skip',
	});

	// Fire the publish event once both the entry point is detected AND the mini-modal
	// is available (i.e. AISmartButton has mounted and set isMiniModalAvailable=true).
	// This avoids the race condition where publish fires before AISmartButton is ready,
	// which would cause a silent fallback to the sidebar.
	// hasFiredRef ensures this is a one-shot action per page load — if isMiniModalAvailable
	// toggles after firing, the event will not fire again.
	useEffect(() => {
		if (!entryPoint || !isMiniModalAvailable || hasFiredRef.current || rovoConfig === null) {
			return;
		}
		hasFiredRef.current = true;
		publish({
			type: 'chat-new',
			openChatMode: 'mini-modal',
			data: {
				dialogues: [],
			},
			source: entryPoint,
		});
	}, [entryPoint, isMiniModalAvailable, publish, rovoConfig]);

	return rovoConfig;
}
